import {makeAutoObservable, runInAction} from 'mobx';
import {IChangeable} from '../../../../../stores/types';
import {
    ICountryZone,
    IMerchantFilteringExchangeLocation, REST_OF_WORLD,
} from '../../../../../models/User';
import {isSet} from '../../../../../models/model-types-methods';
import {IPlatformLocation} from '../../../../return-methods/return-method-types';
import ExchangeLocationsTableBuilder, {IExchangeLocationRuleRow,} from '../utilities/exchange-locations-table-builder';
import MerchantWarehousesZonesService from '../../../../../services/api/merchant-warehouses-zones-service';
import {rootContainer, TYPE} from '../../../../../services/container/root';

/** Return type for getUpdatedModel() */
interface IMerchantFilteringExchangeLocations {
    merchant_filtering_exchange_locations: IMerchantFilteringExchangeLocation[];
}

/**
 * MobX store for managing exchange locations configuration.
 * Implements IChangeable for integration with parent SettingStore.
 * Model: IMerchantFilteringExchangeLocation[]
 */
class ExchangeLocationsStore implements IChangeable<IMerchantFilteringExchangeLocations> {


    private readonly _model: IMerchantFilteringExchangeLocation[];
    private _changes?: IMerchantFilteringExchangeLocation[];

    /** Reference data (loaded from API) */
    private _warehouses: IPlatformLocation[] = [];
    private _countries: ICountryZone[] = [];

    /** Loading state */
    private _isLoading: boolean = false;

    /** Injected utilities */
    private _tableBuilder?: ExchangeLocationsTableBuilder;

    constructor(filteringExchangeLocation: IMerchantFilteringExchangeLocation[]) {
        makeAutoObservable(this, {}, { autoBind: true });

        /** Initialize model from SettingStore */
        this._model = filteringExchangeLocation;
    }

    private get merchantWarehousesZonesService(): MerchantWarehousesZonesService {
        return rootContainer.get<MerchantWarehousesZonesService>(TYPE.MerchantWarehousesZonesService);
    }

    /** Load warehouses and zones options from API */
    public async load(): Promise<void> {
        if (this._isLoading) {
            return;
        }

        try {
            this._isLoading = true;

            const data = await this.merchantWarehousesZonesService.getMerchantWarehousesAndZones();

            runInAction(() => {
                this._warehouses = data.merchant_warehouses;
                this._countries = data.exchange_zones_options;

                /** Initialize table builder after loading data */
                this._tableBuilder = new ExchangeLocationsTableBuilder(
                    this._warehouses,
                    this._countries
                );

                this._isLoading = false;
            });
        }
        catch (error) {
            runInAction(() => {
                this._isLoading = false;
            });
            throw error;
        }
    }

    /** Loading state getter */
    public isLoading(): boolean {
        return this._isLoading;
    }

    /** IChangeable implementation */

    public hasChanges(): boolean {
        return isSet(this._changes);
    }

    public discardChanges(): void {
        this._changes = undefined;
    }

    public getUpdatedModel(): IMerchantFilteringExchangeLocations {
        return {
            merchant_filtering_exchange_locations: this._changes ?? this._model
        };
    }

    /** Get current state (model + changes) for internal use */
    private getLocations(): IMerchantFilteringExchangeLocation[] {
        return this._changes ?? this._model;
    }

    /** Table rows (computed from locations via builder) */

    public getTableRows(): IExchangeLocationRuleRow[] {
        if (!this._tableBuilder) {
            return [];
        }

        return this._tableBuilder.buildTableRows(
            this.getLocations()
        );
    }

    /** Reference data getters */

    public getWarehouses(): IPlatformLocation[] {
        return this._warehouses;
    }

    public getCountries(): ICountryZone[] {
        return this._countries;
    }

    /** Used warehouse IDs in non-fallback rules (for disabling in dropdowns) */

    public getUsedWarehouseIds(): Set<string> {
        const usedIds = new Set<string>();
        this.getLocations().forEach((location) => {
            /** Only count non-fallback rules - fallback rules don't block creating regular rules */
            if (location.predefined_country_group !== REST_OF_WORLD) {
                usedIds.add(location.location_id);
            }
        })
        return usedIds;
    }

    /** Check if all warehouses are used in non-fallback rules (to disable "+ ADD RULE" button) */

    public areAllWarehousesUsed(): boolean {
        if (this._warehouses.length === 0) {
            return true;
        }

        /** Get warehouse IDs that are used in non-fallback rules */
        const usedWarehouseIds = this.getUsedWarehouseIds();

        /** Check if all available warehouses are used */
        return this._warehouses.every((warehouse) => usedWarehouseIds.has(warehouse.location_id));
    }

    /** Actions for managing rules */

    /**
     * Add or update a rule (regular or fallback).
     * If editingRow is null - adds a new rule.
     * If editingRow is provided - removes old row and adds new locations.
     * Called from modal when the user applies changes.
     */
    public upsertRule(
        editingRow: IExchangeLocationRuleRow | null,
        newLocations: IMerchantFilteringExchangeLocation[]
    ): void {

        /** Remove old locations if editing existing row */
        const filtered = editingRow
            ? this.getLocations().filter((location) => !this._isLocationInArray(location, editingRow.locations))
            : this.getLocations();

        /** Add new locations */
        const updatedLocations = [
            ...filtered,
            ...newLocations,
        ];

        if (this._areLocationsEqual(updatedLocations, this._model)) {
            this._changes = undefined;
        }
        else {
            this._changes = updatedLocations;
        }
    }

    /**
     * Delete a rule (row).
     * Called from table when user clicks delete button.
     */
    public deleteRule(row: IExchangeLocationRuleRow): void {
        const currentLocations = this.getLocations();
        const filteredLocations = currentLocations.filter((location) => !this._isLocationInArray(location, row.locations));

        if (this._areLocationsEqual(filteredLocations, this._model)) {
            this._changes = undefined;
        }
        else {
            this._changes = filteredLocations;
        }
    }

    /** Helper methods */

    /** Check if the location exists in an array (by matching warehouse + zone) */
    private _isLocationInArray(
        location: IMerchantFilteringExchangeLocation,
        array: IMerchantFilteringExchangeLocation[]
    ): boolean {
        return array.some(
            (item) =>
                item.location_id === location.location_id &&
                item.predefined_country_group === location.predefined_country_group &&
                item.country_code === location.country_code
        );
    }

    /** Compare two location arrays for equality (changes detection) */
    private _areLocationsEqual(
        currentLocations: IMerchantFilteringExchangeLocation[],
        modelLocations: IMerchantFilteringExchangeLocation[]
    ): boolean {
        if (currentLocations.length !== modelLocations.length) {
            return false;
        }

        /** Create sorted token arrays to handle duplicates correctly */
        const toToken = (location: IMerchantFilteringExchangeLocation) => JSON.stringify({
            location_id: location.location_id,
            predefined_country_group: location.predefined_country_group,
            country_code: location.country_code,
        });

        const currentTokens = currentLocations.map(toToken).sort();
        const modelTokens = modelLocations.map(toToken).sort();

        /** Compare sorted arrays element by element */
        for (let index = 0; index < currentTokens.length; index++) {
            if (currentTokens[index] !== modelTokens[index]) {
                return false;
            }
        }

        return true;
    }
}

export default ExchangeLocationsStore;

